import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Depressurization test — blower door draws air out, infiltration arrows in. */
export function DiagramBETDepressurization() {
  const hx = 75, hy = 75, hw = 210, hh = 150;

  return (
    <DiagramCanvas>
      {/* Roof */}
      <Polygon points={`${hx - 6},${hy} ${hx + hw / 2},${hy - 32} ${hx + hw + 6},${hy}`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      <Rect x={hx + hw - 50} y={hy - 28} width={14} height={18} fill={DT.fill} stroke={DT.stroke} strokeWidth="1" />

      {/* Building */}
      <Rect x={hx} y={hy} width={hw} height={hh} fill="rgba(122,170,187,0.08)" stroke={DT.stroke} strokeWidth="1.8" />

      {/* Blower door (center) */}
      <Rect x={hx + hw / 2 - 30} y={hy + 18} width={60} height={hh - 18} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.6" />
      <Rect x={hx + hw / 2 - 26} y={hy + 22} width={52} height={hh - 26} fill="rgba(255,107,26,0.06)" stroke={DT.varColor} strokeWidth="1" strokeDasharray="3,2" />
      <Circle cx={hx + hw / 2} cy={hy + hh / 2} r={20} fill={DT.fill} stroke={DT.varColor} strokeWidth="1.8" />
      {/* Fan blades (curved, showing rotation) */}
      {[0, 60, 120, 180, 240, 300].map(d => {
        const r = d * Math.PI / 180;
        return (
          <Path key={d} d={`M${hx + hw / 2},${hy + hh / 2} Q${hx + hw / 2 + 10 * Math.cos(r + 0.3)},${hy + hh / 2 + 10 * Math.sin(r + 0.3)} ${hx + hw / 2 + 18 * Math.cos(r)},${hy + hh / 2 + 18 * Math.sin(r)}`}
            fill="none" stroke={DT.varColor} strokeWidth="1.6" />
        );
      })}
      <Circle cx={hx + hw / 2} cy={hy + hh / 2} r={3} fill={DT.varColor} />
      <SvgText x={hx + hw / 2} y={hy + hh - 6} fill={DT.varColor} fontSize="7" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">FAN (exhausts)</SvgText>

      {/* Fan exhaust outside */}
      {[hy + hh / 2 - 6, hy + hh / 2, hy + hh / 2 + 6].map((y, i) => (
        <G key={i}>
          <Line x1={hx + hw / 2 + 20} y1={y} x2={hx + hw + 12} y2={y} stroke={DT.arrow} strokeWidth="1.4" />
          <Polygon points={`${hx + hw + 12},${y} ${hx + hw + 4},${y - 4} ${hx + hw + 4},${y + 4}`} fill={DT.arrow} />
        </G>
      ))}

      {/* Infiltration arrows IN through leaks */}
      {[
        [hx, hy + 40, 1], [hx, hy + 100, 1], [hx, hy + 150, 1],
        [hx + hw, hy + 60, -1], [hx + hw, hy + 120, -1],
      ].map(([lx, ly, dir], i) => (
        <G key={i}>
          <Path d={`M${lx - dir * 1},${ly} l${-dir * 3},4 l${dir * 3},2`} stroke={DT.varColor} strokeWidth="1.2" fill="none" />
          <Line x1={lx - dir * 18} y1={ly} x2={lx - dir * 2} y2={ly} stroke={DT.varColor} strokeWidth="1.4" strokeDasharray="3,2" />
          <Polygon points={`${lx - dir * 2},${ly} ${lx - dir * 8},${ly - 4} ${lx - dir * 8},${ly + 4}`} fill={DT.varColor} />
        </G>
      ))}
      {/* Through roof */}
      {[hx + 60, hx + 160].map((x, i) => (
        <G key={`r${i}`}>
          <Line x1={x} y1={hy - 14} x2={x} y2={hy + 4} stroke={DT.varColor} strokeWidth="1.2" strokeDasharray="3,2" />
          <Polygon points={`${x},${hy + 4} ${x - 4},${hy - 3} ${x + 4},${hy - 3}`} fill={DT.varColor} />
        </G>
      ))}

      {/* Pressure gauge (−50 Pa) */}
      <Rect x={20} y={180} width={54} height={40} rx="4" fill={DT.fill} stroke={DT.varColor} strokeWidth="1.4" />
      <Rect x={24} y={185} width={46} height={16} fill="#001020" />
      <SvgText x={47} y={196} fill="#FF6B1A" fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">-50 Pa</SvgText>
      <SvgText x={47} y={212} fill={DT.desc} fontSize="6" fontFamily={DT.mono} textAnchor="middle">manometer</SvgText>
      <Line x1={74} y1={200} x2={hx} y2={hy + hh / 2} stroke={DT.leader} strokeWidth="0.6" strokeDasharray="2,2" />

      {/* −ΔP badge inside building */}
      <Rect x={hx + 10} y={hy + hh / 2 - 10} width={44} height={20} fill="rgba(0,0,0,0.55)" stroke={DT.varColor} strokeWidth="0.8" />
      <SvgText x={hx + 32} y={hy + hh / 2 + 4} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">−ΔP</SvgText>

      {/* Ground */}
      <Line x1={20} y1={hy + hh} x2={330} y2={hy + hh} stroke={DT.dim} strokeWidth="1.2" />

      {/* Labels */}
      <LeaderLine x1={hx + 8} y1={hy + 40} x2={20} y2={50} varName="Q_in" desc="infiltration" anchor="start" />
      <LeaderLine x1={hx + hw / 2 + 20} y1={hy + hh / 2} x2={335} y2={50} varName="Q_fan" desc="exhaust flow" anchor="end" />
      <LeaderLine x1={47} y1={180} x2={20} y2={235} varName="−ΔP" desc="50 Pa" anchor="start" />

      <FormulaTag formula="Q_in = Q_fan    at steady −50 Pa    (depressurization)" />
    </DiagramCanvas>
  );
}
